import React from 'react';
import * as Babel from '@babel/standalone';
import { listDir } from './filesystem';
import { getTags } from './git';

const HOMEPAGE_ID = '0';

const COMPILED_COMPONENT_CACHE_MAX_ENTRIES = 250;
type CompiledComponentCacheEntry = {
  compileError: string | null;
  compiledComponent: any;
};
// In-memory cache to avoid recompiling unchanged components across page switches.
// Keyed by `${projectName}:${componentId}:${hash(componentSource)}`.
const compiledComponentCache = new Map<string, CompiledComponentCacheEntry>();

const hashString = (value: string) => {
  // Simple non-cryptographic hash (FNV-1a 32-bit) to avoid storing large strings as cache keys.
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    // hash *= 16777619 (with 32-bit overflow)
    hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }
  return hash.toString(16);
};

const setCompiledComponentCache = (key: string, entry: CompiledComponentCacheEntry) => {
  compiledComponentCache.set(key, entry);
  // Evict oldest entries when exceeding max size (Map preserves insertion order).
  while (compiledComponentCache.size > COMPILED_COMPONENT_CACHE_MAX_ENTRIES) {
    const oldestKey = compiledComponentCache.keys().next().value;
    if (!oldestKey) break;
    compiledComponentCache.delete(oldestKey);
  }
};

const _compileComponent = async (componentSource: string) => {
  let compiledComponent = null;
  let compileError = null;

  try {
    // strip the imports
    const cString = componentSource.replace(/import.*?;\n/g, '');

    // compile using babel
    const compiledCode = Babel.transform(cString, {
      presets: ['react', 'typescript'],
      filename: 'file.tsx',
    }).code;

    // import
    const blob = new Blob([compiledCode as any], {
      type: 'text/javascript',
    });
    const url = URL.createObjectURL(blob);
    const module = await import(/* @vite-ignore */ url);
    URL.revokeObjectURL(url);

    // set component to return
    compiledComponent = module.component;
  } catch (err) {
    compiledComponent = null;
    compileError = (err as any).stack;
  }

  return { compileError, compiledComponent };
};

const loadLibraryComponents = async (apiHandler: any) => {
  let components: any = [];

  const response = await apiHandler.getComponentLibrary();

  if (response.status !== 200) throw new Error('Could not load library components');
  let data = (await response.json()).components;

  for (const namespace in data) {
    // only use example namespace for now
    if (namespace !== 'example') continue;

    for (const component of data[namespace]) {
      // strip the imports
      let componentString = component.content['component.tsx'];
      componentString = componentString.replace(/import.*?;\n/g, '');

      let layoutString = component.content['layout.json'];

      const compiledCode = Babel.transform(componentString, {
        presets: ['react', 'typescript'],
        filename: 'file.tsx',
      }).code;
      const blob = new Blob([compiledCode as any], {
        type: 'text/javascript',
      });
      const url = URL.createObjectURL(blob);
      const module = await import(/* @vite-ignore */ url);

      const Component = module.component;

      components.push({
        class: component.name,
        layout: JSON.parse(layoutString),
        component: <Component />,
      });
    }
  }

  return components;
};

const loadProjectPages = async (projectName: string) => {
  const projectRoot = `${window.miDir}/${projectName}`;

  try {
    const pagesLayoutFiles = await listDir(`${projectRoot}/src/pages-layout`);
    // Read and parse all JSON files in pages-layout
    const pagesLayoutPromises = pagesLayoutFiles
      .filter((file: string) => file.endsWith('.json'))
      .map(async (file: string) => {
        try {
          const filePath = `${projectRoot}/src/pages-layout/${file}`;
          const content: string = (await window.pfs.readFile(filePath, 'utf8')) as string;
          const parsedContent = JSON.parse(content);
          // Extract id from filename (remove "page-" prefix and ".json" extension)
          const id = file.replace('page-', '').replace('.json', '');
          return { id, ...parsedContent };
        } catch (err) {
          // This can happen during initial load before sync completes
          console.warn(`Error reading page layout file ${file}:`, err);
          return null;
        }
      });
    return (await Promise.all(pagesLayoutPromises)).filter((layout) => layout !== null) as {
      id: string;
      name: string;
      path: string;
      components: object;
    }[];
  } catch (_err) {
    return [];
  }
};

const loadProjectComponents = async (
  projectName: string,
  pageLookupParam?: { pageId?: string; pagePath?: string }
) => {
  let components: any = [];
  let layout: any = [];

  const projectRoot = `${window.miDir}/${projectName}`;
  const componentDir = `src/ProjectComponents`;

  const pages = await loadProjectPages(projectName);

  let pageId = pageLookupParam?.pageId ?? HOMEPAGE_ID;

  if (pageLookupParam?.pagePath) {
    const page = pages.find((page) => {
      // Remove all leading "/" characters using regex for matching
      return page.path.replace(/^\/+/, '') === pageLookupParam?.pagePath?.replace(/^\/+/, '');
    });
    if (page) {
      pageId = page.id;
    }
  }

  // Prefer page-level layout if present
  let pageLayout: any | null = null;
  try {
    const pageLayoutPath = `${projectRoot}/src/pages-layout/page-${pageId}.json`;
    const pageLayoutContent: string = (await window.pfs.readFile(pageLayoutPath, 'utf8')) as string;
    pageLayout = JSON.parse(pageLayoutContent);
  } catch (_err) {
    pageLayout = null; // fallback to per-component layout.json files
  }

  let componentFiles: string[] = await listDir(`${projectRoot}/${componentDir}`);
  componentFiles = componentFiles.filter(
    (_c: any) => !(_c.includes('.gitkeep') || _c.includes('index.ts'))
  );

  // If page-level layout exists, only compile the components referenced by that layout.
  // This makes page navigation scale with "components on page" instead of "components in project".
  if (pageLayout && pageLayout.components && typeof pageLayout.components === 'object') {
    // Filter out __dropping-elem__ - it's a placeholder used during drag-and-drop
    const neededComponentIds = new Set(
      Object.keys(pageLayout.components).filter((id) => id !== '__dropping-elem__')
    );
    componentFiles = componentFiles.filter((c) => neededComponentIds.has(c));
  }

  // TODO: make this async
  for (const _c of componentFiles) {
    let componentName = `${_c.replace(componentDir, '')}`;

    let sourceFile = `${componentName}/component.tsx`;
    let componentString: string = (await window.pfs.readFile(
      `${projectRoot}/${componentDir}/${sourceFile}`,
      'utf8'
    )) as string;
    const cacheKey = `${projectName}:${_c}:${hashString(componentString)}`;
    const cached = compiledComponentCache.get(cacheKey);
    const { compileError, compiledComponent } = cached
      ? cached
      : await _compileComponent(componentString).then((result) => {
          setCompiledComponentCache(cacheKey, result);
          return result;
        });

    // Only read per-component layout when page-level layout is not present
    let layoutString: string | null = null;
    if (!pageLayout) {
      const layoutFile = `${componentName}/layout.json`;
      try {
        layoutString = (await window.pfs.readFile(
          `${projectRoot}/${componentDir}/${layoutFile}`,
          'utf8'
        )) as string;
      } catch (_err) {
        layoutString = null;
      }
    }

    let componentObj: any = {
      id: _c,
      component: null,
    };

    // if there is a compilation error, no further configuration is needed
    if (compileError) {
      componentObj = {
        ...componentObj,
        overrides: {
          compileError: {
            message: compileError,
          },
        },
      };

      components.push(componentObj);
      if (!pageLayout && layoutString) {
        layout.push({ ...JSON.parse(layoutString), i: _c });
      }
      continue;
    }

    // TODO: impl more general abstraction for handling components with unique
    //  configuration requirements. It's NOT worth doing this yet, as we only
    //  have two component types.
    // we need to handle chart component initialization differently

    // naive assumption that a component must be a chart if it has 'Chart' in
    //  its name
    if (componentName.includes('Chart')) {
      let configFile = `${componentName}/chart_config.json`;
      const configString: string = (await window.pfs.readFile(
        `${projectRoot}/${componentDir}/${configFile}`,
        'utf8'
      )) as string;
      const configObj = JSON.parse(configString);

      const Component = compiledComponent;
      componentObj.component = <Component chartConfig={configObj} />;
    } else {
      const Component = compiledComponent;
      componentObj.component = <Component />;
    }
    components.push(componentObj);
    if (!pageLayout && layoutString) {
      layout.push({ ...JSON.parse(layoutString), i: _c });
    }
  }

  // If page-level layout exists, build layout from it
  if (pageLayout && pageLayout.components && typeof pageLayout.components === 'object') {
    layout = Object.entries(pageLayout.components).map(([id, pos]) => ({
      ...(pos as any),
      i: id,
    }));
  }

  return {
    components: components,
    layout: layout,
    pages,
  };
};

const b64UrlForProjectImage = async (projectName: string, imageFilename: string) => {
  const MIME_TYPES: { [key: string]: string } = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
  };

  // Use readdir to list the projects and do a case insensitive match for projectName
  const projects: string[] = await window.pfs.readdir(window.miDir);
  const matchedProject = projects.find((proj) => proj.toLowerCase() === projectName.toLowerCase());

  if (!matchedProject) {
    throw new Error(`Project "${projectName}" not found`);
  }

  const filePath = `${window.miDir}/${matchedProject}/src/assets/images/${imageFilename}`;
  const data = await window.pfs.readFile(filePath);
  const base64 = Buffer.from(data).toString('base64');

  // Extract the file extension and convert to lowercase
  const extension = imageFilename.split('.').pop()?.toLowerCase() || '';

  // Correctly access the MIME_TYPES object and default to 'image/jpeg' if not found
  const mimeType = MIME_TYPES[extension] ?? 'image/jpeg';

  return `data:${mimeType};base64,${base64}`;
};

const setTailwindTheme = (theme: object) => {
  if ((window as any).tailwind) {
    (window as any).tailwind.config = theme;
  } else {
    throw new Error('Could not load tailwind theme: missing tailwind global.');
  }
};

const useB64UrlForProjectImage = (projectName: string, imageFilename: string) => {
  const [imageUrl, setImageUrl] = React.useState<string>('');

  React.useEffect(() => {
    const fetchImageUrl = async () => {
      try {
        const url = await b64UrlForProjectImage(projectName, imageFilename);
        setImageUrl(url);
      } catch (error) {
        console.error('Error fetching background image URL:', error);
      }
    };

    fetchImageUrl();
  }, [projectName, imageFilename]);

  return imageUrl;
};

const useAppSettings = (projectName: string, deps: any[] = []) => {
  const [appSettings, setAppSettings] = React.useState<any>(null);

  React.useEffect(() => {
    const getAppSettings = async () => {
      setAppSettings(null);
      try {
        const filePath = `${window.miDir}/${projectName}/settings.json`;
        await window.pfs.readFile(filePath, 'utf8').then((fileContent: string | Uint8Array) => {
          if (fileContent instanceof Uint8Array) {
            throw new Error('Expected string but received buffer (Uint8Array)');
          }
          setAppSettings(JSON.parse(fileContent));
        });
      } catch (err) {
        console.log('Error: can not read app settings file.');
        throw err;
      }
    };
    getAppSettings();
  }, [projectName, ...deps]);

  return appSettings;
};

const useAppVersions = (projectName: string, projectURL: string, deps: any[] = []) => {
  const [appVersions, setAppVersions] = React.useState<{
    publishedVersion: { tag: string; targetCommitTimestamp: number; targetCommit: string } | null;
    versions: { tag: string; targetCommitTimestamp: number; targetCommit: string }[];
  } | null>(null);

  React.useEffect(() => {
    const getAppVersions = async () => {
      setAppVersions(null);
      const tagData = await getTags(projectName, projectURL);
      if (tagData) {
        const publishedVersion = tagData.filter((tag) => tag.tag === 'published');
        const versions = tagData.filter((tag) => tag.tag !== 'published');

        setAppVersions({
          publishedVersion: publishedVersion.length ? publishedVersion[0] : null,
          versions,
        });
      }
    };
    getAppVersions();
  }, [projectName, projectURL, ...deps]);

  return appVersions;
};

// Helper to convert Uint8Array to string
const toString = (data: string | Uint8Array): string => {
  if (typeof data === 'string') {
    return data;
  }
  const decoder = new TextDecoder('utf-8');
  return decoder.decode(data);
};

export {
  loadProjectPages,
  loadLibraryComponents,
  loadProjectComponents,
  setTailwindTheme,
  useB64UrlForProjectImage,
  useAppSettings,
  useAppVersions,
  toString,
};
